'use strict';
import get from 'lodash/get';
import {STATE_CHANGE_SUCCESS} from 'redux-ui-router/lib/action-types';
import {epicErrorHandler} from '../../../core-utilities/rx-utils';
import {getId, getLink} from '../../../core-utilities/domain/ReduxUtils';
import {getUserId} from '../../../auth/reducers';
import {constructAssetProjectId} from '../../../projects/domain/ProjectDomain';
import {assetFileRequirementStates, projectFileRequirementStates} from '../../../requirements/router-config';
import {assetTasksStates, projectTasksStates, templateTasksStates} from '../../../tasks/router-config';
import {hydrateProject, hydrateOrganization, selectedAsset, selectedProject} from '../actions';
import {getAsset, getAssetProject, getOrganization, getProject} from '../reducers/state';
import {logger} from './logger';


export const epics = [
  onNavEpic,
];

onNavEpic.$inject = [
  'TaskDomain',
  'AssetTaskDomain',
  'FileRequirementDomain',
  'AssetFileRequirementDomain',
];

/**
 * @param {ActionsObservable<ReduxAction>} action$
 * @param {Store} store
 * @param dependencies
 * @return {Observable.<ReduxAction>}
 */
function onNavEpic(action$, store, dependencies) {
  return action$.ofType(STATE_CHANGE_SUCCESS)
    .map(action => {
      switch (get(action.payload, 'currentState.name')) {
        case assetTasksStates.activityFeed:
        case assetTasksStates.requirements:
        case assetTasksStates.timerLog:
          return {
            collection: dependencies.AssetTaskDomain.COLLECTION,
            id: get(action.payload, 'currentParams.taskId'),
          };
        case projectTasksStates.activityFeed:
        case projectTasksStates.requirements:
        case projectTasksStates.timerLog:
        case templateTasksStates.activityFeed:
        case templateTasksStates.requirements:
          return {
            collection: dependencies.TaskDomain.COLLECTION,
            id: get(action.payload, 'currentParams.taskId'),
          };
        case assetFileRequirementStates.activityFeed:
        case assetFileRequirementStates.details:
          return {
            collection: dependencies.AssetFileRequirementDomain.COLLECTION,
            id: get(action.payload, 'currentParams.requirementId'),
          };
        case projectFileRequirementStates.activityFeed:
        case projectFileRequirementStates.details:
          return {
            collection: dependencies.FileRequirementDomain.COLLECTION,
            id: get(action.payload, 'currentParams.requirementId'),
          };
        default:
          return null;
      }
    })
    .filter(data => data !== null)
    .switchMap(data => {
      switch (data.collection) {
        case dependencies.TaskDomain.COLLECTION:
          return verifyTaskModel(data.id, store, dependencies);

        case dependencies.AssetTaskDomain.COLLECTION:
          return verifyAssetTaskModel(data.id, store, dependencies);

        case dependencies.FileRequirementDomain.COLLECTION:
          return verifyFileRequirementModel(data.id, store, dependencies);

        case dependencies.AssetFileRequirementDomain.COLLECTION:
          return verifyAssetFileRequirementModel(data.id, store, dependencies);
      }
    })
    .filter(Boolean)
    .catch(epicErrorHandler(logger, 'onNavEpic'));
}

function verifyTaskModel(taskId, store, dependencies) {
  return (taskId ? dependencies.TaskDomain.$get(taskId) : Promise.resolve(null))
    .then(task => {
      if (task) {
        const expectedOrg = task.org;
        const expectedProject = getLink(task, 'project').id;
        const rootState = store.getState();
        return syncModel(rootState, expectedOrg, expectedProject, false);
      }
    });
}

function verifyAssetTaskModel(taskId, store, dependencies) {
  return (taskId ? dependencies.AssetTaskDomain.$get(taskId) : Promise.resolve(null))
    .then(task => {
      if (task) {
        const expectedOrg = task.org;
        const expectedProject = getLink(task, 'project').id;
        const expectedAsset = task.ast;
        const rootState = store.getState();
        return syncModel(rootState, expectedOrg, expectedProject, true, expectedAsset);
      }
    });
}

function verifyFileRequirementModel(reqId, store, dependencies) {
  return (reqId ? dependencies.FileRequirementDomain.$get(reqId) : Promise.resolve(null))
    .then(req => {
      if (req) {
        const expectedOrg = req.org;
        const expectedProject = req.prj;
        const rootState = store.getState();
        return syncModel(rootState, expectedOrg, expectedProject, false);
      }
    });
}

function verifyAssetFileRequirementModel(reqId, store, dependencies) {
  return (reqId ? dependencies.AssetFileRequirementDomain.$get(reqId) : Promise.resolve(null))
    .then(req => {
      if (req) {
        const expectedOrg = req.org;
        const expectedProject = req.prj;
        const expectedAsset = req.ast;
        const rootState = store.getState();
        return syncModel(rootState, expectedOrg, expectedProject, true, expectedAsset);
      }
    });
}

/**
 * Figure out what is changing, then apply those changes to the app-model.
 * @param rootState
 * @param nextOrgId
 * @param nextProjectId
 * @param useAsset
 * @param nextAssetId
 * @return {*}
 */
function syncModel(rootState, nextOrgId, nextProjectId, useAsset, nextAssetId = null) {
  const prevOrgId = getId(getOrganization(rootState));
  const prevProjectId = getId(getProject(rootState));
  const prevAssetId = getId(getAsset(rootState));
  const prevAssetProjectId = getId(getAssetProject(rootState));

  const updateOrg = nextOrgId && nextOrgId !== prevOrgId;
  const updateProject = nextProjectId && nextProjectId !== prevProjectId;
  const updateAsset = useAsset && nextAssetId && nextAssetId !== prevAssetId;

  const finalOrgId = updateOrg ? nextOrgId : prevOrgId;
  const finalProjectId = updateProject ? nextProjectId : prevProjectId;
  // If we are not changing the asset or the org, keep the asset the same, otherwise update it regardless
  const finalAssetId = updateAsset ? nextAssetId : (updateOrg ? null : prevAssetId);

  const nextAssetProjectId = finalProjectId && finalAssetId
    ? constructAssetProjectId(finalProjectId, finalAssetId)
    : null;
  const updateAssetProject = prevAssetProjectId !== nextAssetProjectId;
  const finalAssetProjectId = updateAssetProject ? nextAssetProjectId : prevAssetProjectId;

  // Apply the changes found
  if (updateOrg) {
    return hydrateOrganization({
      userId: getUserId(rootState),
      nextOrgId: finalOrgId,
      nextPrjId: finalProjectId,
      nextAstId: finalAssetId,
      nextAssetProjectId: finalAssetProjectId,
    });
  } else if (updateAssetProject) {
    return hydrateProject({
      nextPrjId: finalProjectId,
      nextAstId: finalAssetId,
      nextAssetProjectId: finalAssetProjectId,
    });
  } else if (updateProject) {
    return selectedProject(finalProjectId);
  } else if (useAsset && updateAsset) {
    return selectedAsset(finalAssetId);
  }
}



// WEBPACK FOOTER //
// ./src/js/app/model/epics/onNavValidateModel.js